import { getMessaging, getToken, deleteToken, onMessage, isSupported } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { app, db, auth } from './firebase';

// Push notifications, client side.
//
// One device token per (school, device). The token is the document id: FCM tokens are unique, contain
// no slashes, and using them as the key means re-registering the same device overwrites rather than
// accumulating -- a collection of dead tokens is the usual way this goes wrong, and it looks like a
// rising delivery-failure rate rather than like a bug.
//
// The VAPID public key is a project-level identifier, not a secret; it ships in every client bundle
// by design. Without it getToken() cannot mint anything.
export const VAPID_PUBLIC_KEY = 'BCfOgnknYacYKU-C58DQGADbfyL0J6LBCcKh1C84rFRBf-9677VFQ9xFPAAYagTfne7ocG0M9JHRng9e_SwLhqY';

export const pushSupported = async () => {
  if (!VAPID_PUBLIC_KEY) return false;
  if (!('Notification' in window)) return false;
  return isSupported().catch(() => false);
};

// iOS only delivers web push to a PWA that has been added to the home screen, and asking for
// permission in a Safari tab there simply fails. Worth knowing before offering a button that cannot
// work.
export const pushNeedsInstallFirst = () => {
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches === true
    || window.navigator.standalone === true;
  return iOS && !standalone;
};

const tokenDoc = (schoolId, token) => doc(db, 'schools', schoolId, 'device_token', token);

// Must be called from a user gesture. Browsers refuse a permission prompt that was not asked for,
// and once somebody has denied it there is no way to ask again from code -- which is why this is
// never called on load.
export const enablePush = async (schoolId) => {
  if (!(await pushSupported())) return { ok: false, reason: 'unsupported' };
  if (pushNeedsInstallFirst()) return { ok: false, reason: 'install-first' };

  const permission = await window.Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: permission };

  // Its own scope, and that is the whole fix.
  //
  // A scope can hold exactly one service worker: registering a second script for the same scope
  // REPLACES the first. Both workers were being registered at '/', so pwa.js's app-shell worker and
  // this one took turns evicting each other -- and since the app shell registers on every load, the
  // FCM worker was gone again by the next launch. Messages arrived at a worker that no longer
  // existed, which looks exactly like push being broken.
  //
  // This is the scope the FCM SDK uses by default, so nothing else has to know about it.
  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    scope: '/firebase-cloud-messaging-push-scope',
  });
  const token = await getToken(getMessaging(app), {
    vapidKey: VAPID_PUBLIC_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) return { ok: false, reason: 'no-token' };

  await setDoc(tokenDoc(schoolId, token), {
    token,
    email: (auth.currentUser?.email || '').toLowerCase(),
    platform: navigator.userAgent.slice(0, 180),
    createdAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
  });

  localStorage.setItem('push_token', token);
  return { ok: true, token };
};

export const disablePush = async (schoolId) => {
  const token = localStorage.getItem('push_token');
  try { await deleteToken(getMessaging(app)); } catch { /* already gone */ }
  if (token && schoolId) await deleteDoc(tokenDoc(schoolId, token)).catch(() => {});
  localStorage.removeItem('push_token');
};

export const pushEnabledHere = () =>
  typeof window.Notification !== 'undefined'
  && window.Notification.permission === 'granted'
  && !!localStorage.getItem('push_token');

// A message that arrives while the app is open and in front of somebody. Deliberately NOT shown as a
// system notification: the person is already looking at the app, and the screens refresh themselves.
// The callback lets a module show something inline if it wants to.
export const onForegroundMessage = (fn) => {
  pushSupported().then((ok) => {
    if (ok) onMessage(getMessaging(app), fn);
  });
  return () => {};
};
